import { escapeRegExp } from 'lodash';
import { memo, useCallback, useContext } from 'react';
import { AutocompletePrediction } from '@bridebook/web/app-shared/lib/search/types';
import {
  getCustomPredictionValues,
  isCustomAutocompletePrediction,
} from '@bridebook/web/app-shared/lib/search/utils/custom-autocomplete-predictions';
import { AutocompleteResults } from './autocomplete-results';
import { GooglePredictionsContext, IGooglePredictionsContext } from './context';
import { LocationSuggestionRow } from './location-suggestion-row';

interface LocationSearchResultsProps {
  input: string;
  // Reference to parent element used to add key up event listener.
  parentRef: HTMLDivElement | null;
  onClick(
    prediction: AutocompletePrediction,
    googlePredictions: IGooglePredictionsContext['googlePredictions'],
  ): void;
  onEscape?(e: KeyboardEvent): void;
  onPositionChange?(position: 'top' | 'bottom'): void;
}
const LocationResultsComponent = ({
  onClick,
  parentRef,
  input,
  onEscape,
  onPositionChange,
}: LocationSearchResultsProps) => {
  const { predictions, googlePredictions } = useContext(GooglePredictionsContext);

  const onClickCallback = useCallback(
    (prediction: AutocompletePrediction) => {
      onClick(prediction, googlePredictions);
    },
    [googlePredictions, onClick],
  );

  // show list only if there are any predictions
  return (
    <AutocompleteResults<AutocompletePrediction, typeof googlePredictions>
      input={input}
      items={predictions}
      additional={googlePredictions}
      parentRef={parentRef}
      onClick={onClickCallback}
      onPositionChange={onPositionChange}
      onEscape={onEscape}>
      {(prediction, onClick) => (
        <LocationSuggestionRow
          suggestion={getPredictionWithMarkdown(prediction, input)}
          onClick={onClick}
        />
      )}
    </AutocompleteResults>
  );
};

export const LocationResults = memo(LocationResultsComponent);

const getPredictionWithMarkdown = (prediction: AutocompletePrediction, input?: string) => {
  const regExp = new RegExp(`${escapeRegExp(input)}`, 'gim');
  const addMarkdown = (value?: string) => value?.replace(regExp, '<b>$&</b>') || '';
  const [primaryValue, secondaryValue] = getCustomPredictionValues(prediction);

  if (isCustomAutocompletePrediction(prediction)) {
    return {
      ...prediction,
      mainText: addMarkdown(primaryValue),
      secondaryText: addMarkdown(secondaryValue),
    };
  }

  return {
    ...prediction,
    structured_formatting: {
      ...prediction.structured_formatting,
      main_text: addMarkdown(primaryValue),
      secondary_text: addMarkdown(secondaryValue),
    },
  };
};
